/* SmartStrip.jsx, single-purpose component that resolves the best
   recommendation for the current page/user context and renders it
   inline. Use this anywhere you want a contextual recommendation
   without hard-coding category + copy at the call site.

   Variants:
     <SmartStrip />                  → resolver chooses everything
     <SmartStrip surface="footer" /> → for the site-wide footer rail
     <SmartStrip suppress="conveyancer" />
                                     → opt out of a category (e.g. if a
                                       page already renders the
                                       Conveyancer + Builder bundle)
*/
import React from 'react';
import { useLocation } from 'react-router-dom';
import RecommendationStrip from './RecommendationStrip';
import BundleStrip from './BundleStrip';
import { useAuth } from '../../workspace/context/AuthContext';
import { resolveSmartRecommendation, isPathExcluded } from './smartRecommendationResolver';
import { useUserState } from '../../lib/userState';
import useAffiliateProfileActive from '../../hooks/useAffiliateProfileActive';

export default function SmartStrip({ surface = 'inline', suppress = null, className = '' }) {
  const { pathname } = useLocation();
  const affiliateActive = useAffiliateProfileActive();
  const userState = useUserState();
  // The Auth context throws if used outside its provider on certain
  // marketing-only routes. Wrap in a try/catch so we degrade to an
  // anonymous resolver instead of crashing the page.
  let user = null;
  let profile = null;
  try {
    const auth = useAuth();
    user = auth?.user || null;
    profile = auth?.profile || null;
  } catch (e) {
    // AuthContext not mounted on this branch, treat as logged-out.
  }

  if (isPathExcluded(pathname)) return null;
  if (affiliateActive) return null; // off-message on affiliate shopping pages

  const resolved = resolveSmartRecommendation({
    pathname,
    surface,
    profile,
    isLoggedIn: !!user,
    userState,
  });
  if (!resolved) return null;

  // Allow page-level opt-out, e.g. EstatesIndex already renders a
  // Conveyancer + Builder bundle, so its <SmartFooterStrip /> should
  // skip the same bundle and fall through to a strip.
  if (suppress && resolved.props?.category && resolved.props.category.includes(suppress)) {
    return null;
  }
  if (suppress === 'bundle' && resolved.variant === 'bundle') return null;

  if (resolved.variant === 'bundle') {
    return <div className={className}><BundleStrip {...resolved.props} /></div>;
  }
  return <div className={className}><RecommendationStrip {...resolved.props} /></div>;
}
